import Human from '@vladmandic/human';

export class AiService {
    constructor(preferredBackend = null) {
        this.personsConfidence = 0.2;
        this.phoneConfidence = 0;
        this.similarityConfidence = 0.5;
        this.baseFaceMinScore = 0.8;
        this.baseFaceMinCoverage = 0.06;
        this.baseFaceMaxCenterOffsetX = 0.2;
        this.baseFaceMaxCenterOffsetY = 0.22;
        this.baseFaceMaxYaw = 20 * (Math.PI / 180);
        this.baseFaceMaxPitch = 15 * (Math.PI / 180);
        this.baseFaceMaxRoll = 15 * (Math.PI / 180);
        this.supportedBackends = ['webgl', 'wasm', 'cpu'];
        this.backendOrder = this.getBackendOrder(preferredBackend);
        this.currentBackend = null;
        this.loadPromise = null;
        
        Object.defineProperty(this, 'human', {
            value: null,
            writable: true,
            enumerable: false,
            configurable: true,
        });
        
        this.active = false;
        this.ready = false;

        this.baseFace = null;
        this.baseFaceSrc = null;
        this.baseFaceEmbedding = null;
        this.baseFaceVector = null;
        this.baseFaceError = null;
        this.flags = [];
        this.activeFlags = {
            phoneDetected: false,
            multiplePeople: false,
            faceMismatch: false,
        };

        this.faceMismatchFrames = 0;
        this.faceMatchFrames = 0;
        this.noFaceFrames = 0;
        this.faceMismatchConfirmFrames = 3;
        this.faceMatchClearFrames = 3;
        this.noFaceResetFrames = 10;

        this.faceMatchEstablished = false;
        this.faceMatchEstablishFrames = 3;
        this.faceMismatchWarmupFrames = 15;
        this.warmupFramesRemaining = 0;

        this.debug = {};
        this.similarity = null;
        this.modelLoaded = false;
        this.videoElement = null;
    }

    getBackendOrder(preferredBackend = null) {
        if (!preferredBackend || !this.supportedBackends.includes(preferredBackend)) {
            return [...this.supportedBackends];
        }

        return [
            preferredBackend,
            ...this.supportedBackends.filter((backend) => backend !== preferredBackend),
        ];
    }

    setPreferredBackend(preferredBackend = null) {
        this.backendOrder = this.getBackendOrder(preferredBackend);
        this.currentBackend = null;
        this.loadPromise = null;
        this.modelLoaded = false;
        this.human = null;
        this.ready = false;
        this.baseFace = null;
        this.baseFaceSrc = null;
        this.baseFaceEmbedding = null;
        this.baseFaceVector = null;
        this.baseFaceError = null;
        this.debug = {};
        this.flags = [];
        this.similarity = null;
    }

    createHuman(backend) {
        return new Human({
            backend,
            modelBasePath: '/models/',
            face: {
                enabled: true,
                detector: { modelPath: 'blazeface.json' },
                description: { enabled: true },
                mesh: { enabled: true },
                iris: { enabled: false },
                emotion: { enabled: false },
            },
            hand: { enabled: false },
            gesture: { enabled: false },
            body: { enabled: false },
            object: { enabled: true, modelPath: 'centernet.json' },
        });
    }

    async loadModels() {
        if (this.modelLoaded && this.human) {
            return this.human;
        }
        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = this.loadModelsWithFallback();
        try {
            return await this.loadPromise;
        } finally {
            this.loadPromise = null;
        }
    }

    async loadModelsWithFallback() {
        let lastError = null;

        for (const backend of this.backendOrder) {
            const human = this.createHuman(backend);

            try {
                await human.init();
                await human.load();
                this.human = human;
                this.currentBackend = backend;
                this.modelLoaded = true;
                return human;
            } catch (error) {
                lastError = error;
                this.modelLoaded = false;
                this.human = null;
                this.currentBackend = null;
                // eslint-disable-next-line no-console
                console.error(`Human init failed on ${backend}:`, error);
            }
        }

        throw lastError || new Error('Unable to initialize AI models');
    }

    async reloadWithNextBackend() {
        const currentIndex = this.backendOrder.indexOf(this.currentBackend);
        const fallbackOrder = currentIndex === -1
            ? this.backendOrder
            : this.backendOrder.slice(currentIndex + 1);

        if (!fallbackOrder.length) {
            throw new Error('No AI backend fallback available');
        }

        let lastError = null;
        for (const backend of fallbackOrder) {
            const human = this.createHuman(backend);

            try {
                await human.init();
                await human.load();
                if (this.videoElement) {
                    human.video(this.videoElement);
                }
                this.human = human;
                this.currentBackend = backend;
                this.modelLoaded = true;
                return human;
            } catch (error) {
                lastError = error;
                // eslint-disable-next-line no-console
                console.error(`Human fallback failed on ${backend}:`, error);
            }
        }

        throw lastError || new Error('Unable to recover AI backend');
    }

    isRecoverableBackendError(error) {
        const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';

        return error?.name === 'AbortError'
            || message.includes('mapasync')
            || message.includes('external instance reference')
            || message.includes('webgpu')
            || message.includes('context lost')
            || message.includes('backend');
    }

    getSourceDimensions(source) {
        if (!source) {
            return { width: 0, height: 0 };
        }

        return {
            width: source.width || source.videoWidth || source.clientWidth || 0,
            height: source.height || source.videoHeight || source.clientHeight || 0,
        };
    }

    getPrimaryFace(faces = []) {
        if (!Array.isArray(faces) || faces.length === 0) {
            return null;
        }

        return faces
            .filter(Boolean)
            .sort((faceA, faceB) => (faceB?.faceScore || faceB?.score || 0) - (faceA?.faceScore || faceA?.score || 0))[0] || null;
    }

    isFaceUsable(face, source) {
        if (!face?.embedding || !Array.isArray(face.box) || face.box.length < 4) {
            return false;
        }

        const { width, height } = this.getSourceDimensions(source);
        if (!width || !height) {
            return false;
        }

        const [x, y, boxWidth, boxHeight] = face.box;
        if (!boxWidth || !boxHeight) {
            return false;
        }

        const coverage = (boxWidth * boxHeight) / (width * height);
        if (coverage < this.baseFaceMinCoverage) {
            return false;
        }

        const centerX = (x + (boxWidth / 2)) / width;
        const centerY = (y + (boxHeight / 2)) / height;
        if (Math.abs(centerX - 0.5) > this.baseFaceMaxCenterOffsetX || Math.abs(centerY - 0.5) > this.baseFaceMaxCenterOffsetY) {
            return false;
        }

        const yaw = Math.abs(face.rotation?.angle?.yaw || 0);
        const pitch = Math.abs(face.rotation?.angle?.pitch || 0);
        const roll = Math.abs(face.rotation?.angle?.roll || 0);
        if (yaw > this.baseFaceMaxYaw || pitch > this.baseFaceMaxPitch || roll > this.baseFaceMaxRoll) {
            return false;
        }

        return (face.faceScore || face.score || 0) >= this.baseFaceMinScore;
    }

    async captureBaseFace(videoEl, capturedFrame = null) {
        // Guard: capturing a base face while the continuous detection loop is running
        // can race against Human's internal state and produce confusing results.
        if (this.active) {
            this.baseFaceError = 'errorDetectionActive';
            this.ready = false;
            return { baseFace: this.baseFace, baseFaceSrc: this.baseFaceSrc, ready: this.ready, error: this.baseFaceError };
        }

        if (!this.modelLoaded) {
            await this.loadModels();
        }
        this.baseFace = null;
        this.baseFaceSrc = null;
        this.baseFaceError = null;
        this.baseFaceEmbedding = null;
        this.baseFaceVector = null;
        this.ready = false;

        this.videoElement = videoEl;
        this.human.video(videoEl);
        const detectionSource = capturedFrame || videoEl;
        try {
            // Run the initial detection against the frozen frame captured at click time.
            this.baseFaceEmbedding = await this.human.detect(detectionSource);
        } catch (e) {
            if (this.isRecoverableBackendError(e)) {
                try {
                    await this.reloadWithNextBackend();
                    this.human.video(videoEl);
                    this.baseFaceEmbedding = await this.human.detect(detectionSource);
                } catch (reloadError) {
                    // eslint-disable-next-line no-console
                    console.error('Human backend recovery failed during base capture:', reloadError);
                    this.baseFaceError = 'errorAiSetupFailed';
                    this.ready = false;
                    return { baseFace: this.baseFace, ready: this.ready, error: this.baseFaceError };
                }
            } else {
                // Common failure mode when models load from a wrong relative path (e.g. '../models/' under nested routes).
                // Make it a recoverable UX error instead of crashing the flow.
                // eslint-disable-next-line no-console
                console.error('Human detect failed during base capture:', e);
                this.baseFaceError = 'errorNoFace';
                this.ready = false;
                return { baseFace: this.baseFace, ready: this.ready, error: this.baseFaceError };
            }
        }

        // For the UI preview/upload we want a true still frame.
        // Prefer the frame captured at click time so setup latency cannot shift the preview/upload.
        this.baseFace = this.cloneCanvas(capturedFrame)
            || this.captureCanvasFromVideo(videoEl)
            || this.cloneCanvas(this.baseFaceEmbedding?.canvas ?? null);
        this.baseFaceSrc = this.baseFace ? this.baseFace.toDataURL('image/png') : null;

        const faces = this.baseFaceEmbedding?.face || [];
        const objects = this.baseFaceEmbedding?.object || [];
        const primaryFace = this.getPrimaryFace(faces);

        // Check for cell phones in shot
        const phones = objects.filter((obj) => obj.label === 'cell phone' && obj.score >= this.phoneConfidence);
        if (phones.length > 0) {
            this.baseFaceError = 'errorPhoneDetected';
        } else if (faces.length === 0) {
            this.baseFaceError = 'errorNoFace';
        } else if (faces.length > 1) {
            this.baseFaceError = 'errorMultipleFaces';
        } else if (!this.isFaceUsable(primaryFace, detectionSource)) {
            this.baseFaceError = 'errorNoFace';
        } else {
            // Clone base embedding so future detections can't mutate/reuse the same underlying array.
            this.baseFaceVector = this.cloneEmbedding(primaryFace.embedding);
        }

        this.ready = !this.baseFaceError;
        return { baseFace: this.baseFace, baseFaceSrc: this.baseFaceSrc, ready: this.ready, error: this.baseFaceError };
    }

    cloneCanvas(canvas) {
        if (!canvas) return null;
        const outCanvas = document.createElement('canvas');
        outCanvas.width = canvas.width;
        outCanvas.height = canvas.height;
        const outCtx = outCanvas.getContext('2d');
        if (!outCtx) return null;
        outCtx.drawImage(canvas, 0, 0);
        return outCanvas;
    }

    captureCanvasFromVideo(videoEl) {
        if (!videoEl) return null;
        const width = videoEl.videoWidth || videoEl.clientWidth;
        const height = videoEl.videoHeight || videoEl.clientHeight;
        if (!width || !height) return null;

        const outCanvas = document.createElement('canvas');
        outCanvas.width = width;
        outCanvas.height = height;
        const outCtx = outCanvas.getContext('2d');
        if (!outCtx) return null;
        outCtx.drawImage(videoEl, 0, 0, width, height);
        return outCanvas;
    }

    async start() {
        if (!this.ready || this.active) {
            return;
        }

        // Ensure models are loaded before starting detection loop
        try {
            if (!this.modelLoaded) {
                await this.loadModels();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Human failed to start:', error);
            this.active = false;
            throw error;
        }

        this.active = true;

        // Reset per-run state so we don't carry flicker counters across sessions.
        this.faceMismatchFrames = 0;
        this.faceMatchFrames = 0;
        this.noFaceFrames = 0;
        this.faceMatchEstablished = false;
        this.warmupFramesRemaining = this.faceMismatchWarmupFrames;

        this.detect();
    }

    setVideoElement(videoEl) {
        this.videoElement = videoEl;
        if (this.human) {
            this.human.video(videoEl);
        }
    }

    stop() {
        this.active = false;
    }

    reset() {
        this.stop();
        this.ready = false;
        this.baseFace = null;
        this.baseFaceSrc = null;
        this.baseFaceEmbedding = null;
        this.baseFaceVector = null;
        this.baseFaceError = null;
        this.debug = {};
        this.flags = [];
        this.activeFlags = {
            phoneDetected: false,
            multiplePeople: false,
            faceMismatch: false,
        };
        this.similarity = null;

        this.faceMismatchFrames = 0;
        this.faceMatchFrames = 0;
        this.noFaceFrames = 0;
        this.faceMatchEstablished = false;
        this.warmupFramesRemaining = 0;
        this.videoElement = null;
    }

    async detect() {
        if (!this.active || !this.modelLoaded || !this.human || !this.videoElement) return;

        try {
            const result = await this.human.detect(this.videoElement);
            this.analyzeDetectionResult(result);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Human detection error:', e);
            if (this.isRecoverableBackendError(e)) {
                try {
                    await this.reloadWithNextBackend();
                } catch (reloadError) {
                    // eslint-disable-next-line no-console
                    console.error('Human backend recovery failed during detection loop:', reloadError);
                    this.stop();
                }
            }
        }

        if (this.active) {
            requestAnimationFrame(() => this.detect());
        }
    }

    analyzeDetectionResult(result) {
        this.debug = {};
        if (result.object && result.object.length > 0) {
            const phones = result.object.filter((obj) => obj.label === 'cell phone' && obj.score >= this.phoneConfidence);
            this.debug.phones = phones;
            if (!this.activeFlags.phoneDetected && phones.length > 0) {
                this.activeFlags.phoneDetected = true;
                const preview = this.getPreviewFromCanvas(result.canvas, phones.map((o) => o.box));
                this.flags.push({
                    type: 'phoneDetected',
                    preview,
                });
            } else if (this.activeFlags.phoneDetected && (phones.length === 0 || phones.every((p) => p.score < this.phoneConfidence))) {
                this.activeFlags.phoneDetected = false;
            }

            const people = result.object.filter((obj) => obj.label === 'person' && obj.score >= this.personsConfidence);
            this.debug.people = people;
            if (!this.activeFlags.multiplePeople && people.length > 1) {
                this.activeFlags.multiplePeople = true;
                const preview = this.getPreviewFromCanvas(result.canvas, people.map((o) => o.box));
                this.flags.push({
                    type: 'multiplePeople',
                    preview,
                });
            } else if (this.activeFlags.multiplePeople && (people.length <= 1 || people.every((p) => p.score < this.personsConfidence))) {
                this.activeFlags.multiplePeople = false;
            }
        } else {
            this.activeFlags.phoneDetected = false;
            this.activeFlags.multiplePeople = false;
        }

        const faces = Array.isArray(result.face) ? result.face : [];
        const primaryFace = this.getPrimaryFace(faces);
        this.debug.faceCount = faces.length;
        this.debug.faceUsable = this.isFaceUsable(primaryFace, this.videoElement);

        // If face detection drops out temporarily, don't immediately flip states.
        if (faces.length === 0 || !primaryFace || !this.debug.faceUsable) {
            this.noFaceFrames += 1;
            this.faceMismatchFrames = 0;
            this.faceMatchFrames = 0;

            if (this.noFaceFrames >= this.noFaceResetFrames) {
                this.activeFlags.faceMismatch = false;
                this.similarity = null;
            }
            return;
        }

        this.noFaceFrames = 0;

        // Compute similarity against the captured base embedding.
        if (this.baseFaceVector && primaryFace?.embedding) {
            this.similarity = this.human.match.similarity(this.baseFaceVector, primaryFace.embedding);
        } else {
            this.similarity = null;
        }
        this.debug.similarity = this.similarity;

        const mismatchNow = this.similarity !== null && this.similarity < this.similarityConfidence;

        // Warm up for a short period after starting detection to avoid initial false positives
        // while exposure/positioning stabilizes.
        if (this.warmupFramesRemaining > 0) {
            this.warmupFramesRemaining -= 1;
            if (!mismatchNow) {
                this.faceMatchFrames += 1;
                if (this.faceMatchFrames >= this.faceMatchEstablishFrames) {
                    this.faceMatchEstablished = true;
                }
            }
            return;
        }

        if (mismatchNow) {
            // Don't allow a mismatch until we've observed a stable match against the baseline.
            if (!this.faceMatchEstablished) {
                return;
            }
            this.faceMismatchFrames += 1;
            this.faceMatchFrames = 0;

            if (!this.activeFlags.faceMismatch && this.faceMismatchFrames >= this.faceMismatchConfirmFrames) {
                this.activeFlags.faceMismatch = true;
                const preview = this.getPreviewFromCanvas(result.canvas, faces.map((f) => f.box));
                this.flags.push({
                    type: 'faceMismatch',
                    preview,
                });
            } else if (this.activeFlags.faceMismatch) {
                // keep it set
                this.activeFlags.faceMismatch = true;
            }
        } else {
            this.faceMatchFrames += 1;
            this.faceMismatchFrames = 0;

            if (!this.faceMatchEstablished && this.faceMatchFrames >= this.faceMatchEstablishFrames) {
                this.faceMatchEstablished = true;
            }

            if (this.activeFlags.faceMismatch && this.faceMatchFrames >= this.faceMatchClearFrames) {
                this.activeFlags.faceMismatch = false;
            }
        }
    }

    getPreviewFromCanvas(canvas, boxes) {
        if (!canvas) return null;
        const outCanvas = document.createElement('canvas');
        outCanvas.width = canvas.width;
        outCanvas.height = canvas.height;
        const outCtx = outCanvas.getContext('2d');
        if (!outCtx) return null;

        outCtx.drawImage(canvas, 0, 0);
        outCtx.strokeStyle = '#ff0000';
        outCtx.lineWidth = 4;

        if (Array.isArray(boxes)) {
            boxes.forEach((box) => {
                if (box && box.length === 4) {
                    outCtx.strokeRect(box[0], box[1], box[2], box[3]);
                }
            });
        }

        return outCanvas;
    }

    cloneEmbedding(embedding) {
        if (!embedding) return null;
        // Human embeddings are typically Float32Array; keep the same type when cloning.
        if (typeof embedding.slice === 'function') return embedding.slice(0);
        if (Array.isArray(embedding)) return embedding.slice(0);
        try {
            return Array.from(embedding);
        } catch (e) {
            return null;
        }
    }
}
